import { motion } from 'framer-motion';

const DPVisualization = ({ step, lessonId }) => {
  if (!step) return null;

  // ============ 1D DP TABLE (Fibonacci Memo) ============
  if (step.dimensions === '1D') {
    const { dpTable = [], currentIndex, dependencies = [] } = step;

    return (
      <div className="space-y-4">
        <div className="text-[#FFB800] font-code text-xs mb-2">DP TABLE (1D)</div>
        <div className="flex flex-wrap gap-2 justify-center">
          {dpTable.map((value, index) => {
            const isCurrent = index === currentIndex;
            const isDep = dependencies.includes(index);
            let styleClass = 'border-white/20 bg-[#1A1A20] text-white';
            if (isCurrent) styleClass = 'border-[#FFB800] bg-[#FFB800]/20 text-[#FFB800] shadow-[0_0_15px_rgba(255,184,0,0.5)]';
            else if (isDep) styleClass = 'border-[#007AFF] bg-[#007AFF]/20 text-[#007AFF]';
            else if (value !== null && value !== undefined && !isNaN(value) && index <= currentIndex) styleClass = 'border-green-500/50 bg-green-500/10 text-green-400';

            return (
              <motion.div
                key={index}
                layout
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className={`w-14 h-14 flex flex-col items-center justify-center border-2 rounded-md font-code relative ${styleClass}`}
              >
                <div className="absolute -top-4 text-[9px] text-[#71717A]">dp[{index}]</div>
                <div className="text-lg font-bold">{value === null || value === undefined ? '?' : value}</div>
              </motion.div>
            );
          })}
        </div>

        {step.result !== undefined && (
          <div className="bg-gradient-to-r from-green-500/10 to-green-400/10 border border-green-500/30 rounded-lg p-4 text-center mt-4">
            <div className="text-[#A1A1AA] text-sm font-body mb-1">Final Result</div>
            <div className="text-green-400 text-3xl font-code font-bold">{step.result}</div>
          </div>
        )}
      </div>
    );
  }

  // ============ 2D DP TABLE (LCS / Knapsack) ============
  const { dpTable2D = [], currentCell, dependencies = [] } = step;
  const s1 = step.s1;
  const s2 = step.s2;
  const isLCS = lessonId === 'lcs';

  const getCellStyle = (i, j) => {
    const isCurrent = currentCell && currentCell[0] === i && currentCell[1] === j;
    const isDep = dependencies.some(d => d[0] === i && d[1] === j);
    if (isCurrent) return 'border-[#FFB800] bg-[#FFB800]/20 text-[#FFB800] shadow-[0_0_10px_rgba(255,184,0,0.4)]';
    if (isDep) return 'border-[#007AFF] bg-[#007AFF]/20 text-[#007AFF]';
    if (i === 0 || j === 0) return 'border-white/10 bg-[#0A0A0A] text-[#71717A]';
    return 'border-white/10 bg-[#1A1A20] text-white';
  };

  return (
    <div className="space-y-4">
      <div className="text-[#FFB800] font-code text-xs">DP TABLE (2D)</div>
      
      {/* LCS String Headers */}
      {isLCS && s1 && s2 && (
        <div className="flex gap-3 text-sm font-code">
          <div className="text-[#007AFF]">s1: <span className="text-white">{s1}</span></div>
          <div className="text-[#FFB800]">s2: <span className="text-white">{s2}</span></div>
        </div>
      )}

      {/* Knapsack Item List */}
      {step.weights && step.values && (
        <div className="bg-[#0F0F12] border border-white/10 rounded p-2 text-xs font-code space-y-1">
          <div className="text-[#71717A]">Items: {step.weights.map((w, i) => `(w=${w},v=${step.values[i]})`).join(' ')}</div>
          <div className="text-[#71717A]">Capacity: <span className="text-[#FFB800]">{step.capacity}</span></div>
        </div>
      )}

      <div className="overflow-auto max-h-72">
        <table className="border-collapse mx-auto">
          <tbody>
            {isLCS && (
              <tr>
                <td className="w-8 h-8" />
                <td className="w-8 h-8" />
                {s2?.split('').map((ch, j) => (
                  <td key={j} className="w-8 h-8 text-center font-code text-[#FFB800] text-xs font-bold">{ch}</td>
                ))}
              </tr>
            )}
            {dpTable2D.map((row, i) => (
              <tr key={i}>
                {isLCS && (
                  <td className="w-8 h-8 text-center font-code text-[#007AFF] text-xs font-bold">
                    {i > 0 ? s1?.[i - 1] : ''}
                  </td>
                )}
                {row.map((cell, j) => (
                  <td
                    key={j}
                    className={`border-2 rounded-sm text-xs font-code text-center w-8 h-8 ${getCellStyle(i, j)}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {step.result !== undefined && (
        <div className="bg-gradient-to-r from-green-500/10 to-green-400/10 border border-green-500/30 rounded-lg p-4 text-center">
          <div className="text-[#A1A1AA] text-sm font-body mb-1">
            {isLCS ? 'LCS Length' : 'Max Value'}
          </div>
          <div className="text-green-400 text-3xl font-code font-bold">{step.result}</div>
        </div>
      )}
    </div>
  );
};

export default DPVisualization;
